import { useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Card, EmptyState, NodeAvatar, Pill, showToast } from "../components/primitives";
import type { Persona } from "../components/primitives";
import { getAgentPersona } from "../components/cockpit/CockpitHeader";
import { NewTableModal } from "../components/table/NewTableModal";
import { ChangeOwnerModal } from "../components/table/ChangeOwnerModal";
import {
  useEntityTypes,
  useTableNodes,
  useListTree,
  useWorkspaceInfo,
} from "../../lib/hooks/use-kuvio";
import { useMoveNode } from "../hooks/use-move-node";
import { mcpClient } from "../../lib/mcp-client";
import { useAuth } from "../../lib/hooks/use-auth";
import { useChatScope } from "../hooks/use-chat-scope";
import type { EntityTypeDefinition, LibraryNode } from "../../lib/types";

const NESTED_TABLES_BANNER_KEY = "kuvio_nested_tables_banner_dismissed";

type SortMode = "recent" | "alpha";

function relativeTime(iso: string | undefined): string {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface TableRowData {
  type: EntityTypeDefinition;
  tableNodeId: string | undefined;
  ownerAgentId: string | undefined;
  recordCount: number;
  updatedAt: string | undefined;
}

/**
 * `/tables` — the workspace-wide structured-data rollup (Sprint 9). The
 * counterpart to `/agents`: every table, its record count, owning agent, and
 * last-modified, in one flat list. Tables are workspace-scoped data; ownership
 * is metadata (`owner_agent_id`), not tree location — so this view reads from
 * `list_entity_types` (the canonical list + counts + owner) joined with table
 * nodes (for last-modified) and the agent tree (to name owners).
 */
export function TablesDashboardPage() {
  const { user, currentWorkspaceId } = useAuth();
  const isOwner = user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role === "owner";
  const entityTypes = useEntityTypes();
  const tableNodes = useTableNodes();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  // Sprint 23.9 (Fix 6 / S23.9.22) — explicit workspace scope (was a fall-through).
  useChatScope({ kind: "workspace", label: "Tables" }, null, []);
  const workspace = useWorkspaceInfo();
  const moveNode = useMoveNode();
  const navigate = useNavigate();

  const [sort, setSort] = useState<SortMode>("recent");
  const [ownerFilter, setOwnerFilter] = useState<string>("all"); // "all" | "unowned" | <agentId>
  const [modalOpen, setModalOpen] = useState(false);
  const [ownerModalSlug, setOwnerModalSlug] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem(NESTED_TABLES_BANNER_KEY) === "1"; } catch { return false; }
  });
  const [movingAll, setMovingAll] = useState(false);

  const rootNodeId = workspace.data?.root_node_id ?? undefined;

  // Sprint 14c (S14C.23): tables whose node is nested under an agent rather than
  // at workspace root. They're already visible workspace-wide via this list, but
  // re-parenting to root makes that obvious (and sets owner_agent_id to match).
  const nestedTables = useMemo(
    () =>
      (tableNodes.data ?? []).filter(
        (n) => n.parent_id && rootNodeId && n.parent_id !== rootNodeId,
      ),
    [tableNodes.data, rootNodeId],
  );

  async function handleMoveAllToRoot() {
    if (!rootNodeId) return;
    setMovingAll(true);
    try {
      for (const node of nestedTables) {
        const formerParent = node.parent_id!;
        await moveNode.mutateAsync({ id: node.id, new_parent_id: rootNodeId });
        if (node.entity_type_slug) {
          await mcpClient.toolsCall("set_table_owner", {
            entity_type_slug: node.entity_type_slug,
            owner_agent_id: formerParent,
          });
        }
      }
      await Promise.all([tableNodes.refetch(), entityTypes.refetch(), tree.refetch()]);
      showToast(`Moved ${nestedTables.length} table${nestedTables.length === 1 ? "" : "s"} to workspace root`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't move tables", "error", true);
    } finally {
      setMovingAll(false);
    }
  }

  function dismissBanner() {
    setBannerDismissed(true);
    try { localStorage.setItem(NESTED_TABLES_BANNER_KEY, "1"); } catch { /* ignore */ }
  }

  // entity_type_slug → table node (for last-modified + a navigation fallback).
  const tableNodeBySlug = useMemo(() => {
    const map = new Map<string, LibraryNode>();
    for (const n of tableNodes.data ?? []) {
      if (n.entity_type_slug) map.set(n.entity_type_slug, n);
    }
    return map;
  }, [tableNodes.data]);

  // Agent/folder lookup for resolving owner badges.
  const agentById = useMemo(() => {
    const map = new Map<string, LibraryNode>();
    for (const n of tree.data ?? []) map.set(n.id, n);
    return map;
  }, [tree.data]);

  const rows = useMemo<TableRowData[]>(() => {
    return (entityTypes.data ?? []).map((type) => {
      const node = type.slug ? tableNodeBySlug.get(type.slug) : undefined;
      return {
        type,
        tableNodeId: type._table_node_id ?? node?.id,
        ownerAgentId: type.owner_agent_id ?? undefined,
        recordCount: type._record_count ?? 0,
        updatedAt: node?.updated_at,
      };
    });
  }, [entityTypes.data, tableNodeBySlug]);

  // Owner-filter options: each agent that owns ≥1 table, plus "unowned".
  const ownerOptions = useMemo(() => {
    const owners = new Map<string, string>(); // id → name
    let hasUnowned = false;
    for (const r of rows) {
      if (r.ownerAgentId) {
        const agent = agentById.get(r.ownerAgentId);
        owners.set(r.ownerAgentId, agent?.name ?? "Unknown agent");
      } else {
        hasUnowned = true;
      }
    }
    return { owners: [...owners.entries()], hasUnowned };
  }, [rows, agentById]);

  const visibleRows = useMemo(() => {
    let filtered = rows;
    if (ownerFilter === "unowned") {
      filtered = rows.filter((r) => !r.ownerAgentId);
    } else if (ownerFilter !== "all") {
      filtered = rows.filter((r) => r.ownerAgentId === ownerFilter);
    }
    const copy = [...filtered];
    if (sort === "alpha") {
      copy.sort((a, b) => a.type.name.localeCompare(b.type.name));
    } else {
      copy.sort((a, b) => {
        const av = a.updatedAt ? Date.parse(a.updatedAt) : 0;
        const bv = b.updatedAt ? Date.parse(b.updatedAt) : 0;
        return bv - av;
      });
    }
    return copy;
  }, [rows, ownerFilter, sort]);

  function openTable(row: TableRowData) {
    if (!row.tableNodeId) return;
    const parent = row.ownerAgentId ?? rootNodeId;
    if (!parent) return;
    navigate(`/agents/${parent}/tables/${row.tableNodeId}`);
  }

  const isLoading = entityTypes.isLoading || tableNodes.isLoading;

  return (
    <div style={{ maxWidth: 820 }}>
      {isOwner && !bannerDismissed && nestedTables.length > 0 && (
        <div style={bannerStyle}>
          <i className="ti ti-info-circle" style={{ fontSize: 16, color: "var(--color-text-info)", flexShrink: 0 }} aria-hidden="true" />
          <span style={{ flex: 1, fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
            {nestedTables.length} table{nestedTables.length === 1 ? " is" : "s are"} nested under agents.
            They're already visible workspace-wide via this list, but moving them to workspace root makes
            that obvious (the owning agent is preserved via ownership metadata).
          </span>
          <button type="button" onClick={handleMoveAllToRoot} disabled={movingAll} style={bannerActionBtn}>
            {movingAll ? "Moving…" : "Move all to workspace root"}
          </button>
          <button type="button" onClick={dismissBanner} disabled={movingAll} aria-label="Dismiss" style={bannerDismissBtn}>
            <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden="true" />
          </button>
        </div>
      )}
      <div style={headerStyle}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Tables</h1>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 }}>
            Your workspace's structured data — customers, opportunities, products, anything else you track in rows.
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" onClick={() => setSort("recent")} style={sortBtn(sort === "recent")}>
            Recent
          </button>
          <button type="button" onClick={() => setSort("alpha")} style={sortBtn(sort === "alpha")}>
            A–Z
          </button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
          <button type="button" onClick={() => setOwnerFilter("all")} style={filterPill(ownerFilter === "all")}>
            All
          </button>
          {ownerOptions.hasUnowned && (
            <button type="button" onClick={() => setOwnerFilter("unowned")} style={filterPill(ownerFilter === "unowned")}>
              Unowned
            </button>
          )}
          {ownerOptions.owners.map(([id, name]) => (
            <button key={id} type="button" onClick={() => setOwnerFilter(id)} style={filterPill(ownerFilter === id)}>
              {name}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => setModalOpen(true)} style={newButtonStyle}>
            <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" />
            New table
          </button>
        </div>
      )}

      {isLoading ? (
        <Card>
          <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Loading tables…</span>
          </div>
        </Card>
      ) : entityTypes.error ? (
        <Card>
          <EmptyState>Couldn't load tables.</EmptyState>
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <button onClick={() => entityTypes.refetch()} style={retryBtn}>Retry</button>
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState>
            <div>No tables yet.</div>
            <button onClick={() => setModalOpen(true)} style={{ ...newButtonStyle, margin: "10px auto 0" }}>
              <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" />
              New table
            </button>
          </EmptyState>
        </Card>
      ) : visibleRows.length === 0 ? (
        <Card>
          <EmptyState>No tables match this filter.</EmptyState>
        </Card>
      ) : (
        <Card>
          {visibleRows.map((row) => {
            const owner = row.ownerAgentId ? agentById.get(row.ownerAgentId) : undefined;
            const ownerPersona: Persona | undefined = owner
              ? getAgentPersona({ path: owner.path, agent_config: owner.agent_config as Record<string, unknown> | null })
              : undefined;
            return (
              <div key={row.type.slug} style={rowStyle}>
                <i className="ti ti-table" style={{ fontSize: 18, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
                <div style={{ minWidth: 0, flexShrink: 0, width: 170 }}>
                  <button
                    type="button"
                    onClick={() => openTable(row)}
                    style={nameBtn}
                    title={row.type.name}
                    disabled={!row.tableNodeId}
                  >
                    {row.type.name}
                  </button>
                </div>

                <span style={{ ...mutedText, whiteSpace: "nowrap", width: 70, flexShrink: 0 }}>
                  {row.recordCount} {row.recordCount === 1 ? "row" : "rows"}
                </span>

                <div style={{ flex: 1, display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                  {owner ? (
                    isOwner ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOwnerModalSlug(row.type.slug); }}
                        style={ownerPillBtn}
                        title="Change owner"
                      >
                        <Pill variant={ownerPersona ?? "teal"}>
                          <NodeAvatar type="agent" persona={ownerPersona} size="xs" name={owner.name} />
                          <span style={{ marginLeft: 4 }}>{owner.name}</span>
                        </Pill>
                      </button>
                    ) : (
                      <Pill variant={ownerPersona ?? "teal"}>
                        <NodeAvatar type="agent" persona={ownerPersona} size="xs" name={owner.name} />
                        <span style={{ marginLeft: 4 }}>{owner.name}</span>
                      </Pill>
                    )
                  ) : isOwner ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setOwnerModalSlug(row.type.slug); }}
                      style={ownerPillBtn}
                      title="Change owner"
                    >
                      <span style={{ ...mutedText, fontStyle: "italic" }}>workspace-scoped</span>
                    </button>
                  ) : (
                    <span style={{ ...mutedText, fontStyle: "italic" }}>workspace-scoped</span>
                  )}
                  <span style={slugPill} title={`entity type: ${row.type.slug}`}>{row.type.slug}</span>
                </div>

                <span style={{ ...mutedText, whiteSpace: "nowrap", flexShrink: 0 }} title={row.updatedAt ? `Last modified ${row.updatedAt}` : "No timestamp"}>
                  {relativeTime(row.updatedAt)}
                </span>
              </div>
            );
          })}
        </Card>
      )}

      <NewTableModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        ownerAgentId={rootNodeId ?? ""}
        onCreated={(tableNodeId) => {
          entityTypes.refetch();
          tableNodes.refetch();
          if (rootNodeId) navigate(`/agents/${rootNodeId}/tables/${tableNodeId}`);
        }}
      />
      {ownerModalSlug && (() => {
        const target = rows.find((r) => r.type.slug === ownerModalSlug);
        if (!target) return null;
        return (
          <ChangeOwnerModal
            open
            onClose={() => setOwnerModalSlug(null)}
            entityTypeSlug={target.type.slug}
            entityTypeName={target.type.name}
            currentOwnerAgentId={target.ownerAgentId}
          />
        );
      })()}
    </div>
  );
}

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginBottom: 14,
};

const bannerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  marginBottom: 14,
  background: "var(--c-blue-50, var(--color-background-secondary))",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  fontFamily: "var(--font-sans)",
};

const bannerActionBtn: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: "5px 12px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--c-blue-600, var(--color-text-info))",
  color: "#fff",
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontFamily: "var(--font-sans)",
  flexShrink: 0,
};

const bannerDismissBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  padding: 2,
  display: "inline-flex",
  flexShrink: 0,
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 8px",
  borderBottom: "0.5px solid var(--color-border-secondary)",
  fontFamily: "var(--font-sans)",
};

const nameBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-primary)",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  padding: 0,
  fontFamily: "var(--font-sans)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  textAlign: "left",
};

const mutedText: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
};

const slugPill: CSSProperties = {
  fontSize: 10,
  color: "var(--color-text-tertiary)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "1px 6px",
  fontFamily: "var(--font-mono)",
  whiteSpace: "nowrap",
};

const ownerPillBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  borderRadius: "var(--border-radius-sm)",
  outline: "none",
};

const newButtonStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const retryBtn: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

function sortBtn(on: boolean): CSSProperties {
  return {
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: "var(--border-radius-sm)",
    border: on ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
    background: on ? "var(--c-blue-50)" : "var(--color-background-primary)",
    color: on ? "var(--color-text-info)" : "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontWeight: on ? 500 : 400,
  };
}

function filterPill(on: boolean): CSSProperties {
  return {
    fontSize: 12,
    padding: "4px 12px",
    borderRadius: 999,
    border: on ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
    background: on ? "var(--c-blue-50)" : "var(--color-background-primary)",
    color: on ? "var(--color-text-info)" : "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    fontWeight: on ? 500 : 400,
  };
}
